import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { IOverlayLoaderStore } from '../../../modules/common/OverlayLoader.store';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ACDLError, ForbiddenError } from '../../models/error.model';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IProjectsStore, ProjectOwnership } from './projects.store';
import { IProjectsApiService } from './services/projectApi.service';

interface IProjectsService {
  store: IProjectsStore;
  changeOrder(orderBy: string | undefined, orderType: string | undefined): Promise<void>;
  changeOwnershipFilter(ownership: ProjectOwnership): Promise<void>;
  changePagination(pageNumber: number, pageSize: number): Promise<void>;
  deleteProjectAsync(projectId: string, deleteDatasets: boolean): Promise<void>;
  getProjectExclusiveDatasetsAsync(projectId: string): Promise<string[]>;
  importProjectAsync(projectDraftId: string, importAnnotations: boolean, importImageSets: boolean, file: File): Promise<boolean>;
  refreshAsync(): Promise<void | ACDLError>;
}

export const useProjectsService = (): IProjectsService => {
  const store = useInjection(IProjectsStore.$);
  const notificationsService = useInjection(INotificationsService.$);
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);
  const projectsApiService = useInjection(IProjectsApiService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const changeOwnershipFilter = async (ownership: ProjectOwnership): Promise<void> => {
    if (ownership !== store.projectOwnership) {
      store.projectOwnership = ownership;
      store.projectsPaging.pageNumber = 1;
      await refreshAsync();
    }
  };

  const changeOrder = async (orderBy: string | undefined, orderType: string | undefined): Promise<void> => {
    store.projectsPaging.orderBy = orderBy || '';
    store.projectsPaging.orderType = orderType || '';
    await refreshAsync();
  };

  const changePagination = async (pageNumber: number, pageSize: number): Promise<void> => {
    store.projectsPaging.pageNumber = pageNumber;
    store.projectsPaging.pageSize = pageSize;
    await refreshAsync();
  };

  const refreshAsync = async (): Promise<void> => {
    const { currentWorkspace } = currentWorkspaceStore;
    if (!currentWorkspace.id) {
      return;
    }
    overlayLoaderStore.enableLoader('project-list');
    await getProjectsAsync(currentWorkspace.id);
    overlayLoaderStore.disableLoader('project-list');
  };

  const getProjectsAsync = async (workspaceId: string): Promise<void> => {
    const { pageNumber, pageSize } = store.projectsPaging;
    const { orderBy, orderType } = store.projectsPaging;

    const request = {
      workspaceId,
      pageNumber,
      pageSize,
      orderBy,
      orderType,
      ownership: store.projectOwnership,
    };

    const result = await projectsApiService.getProjectsAsync(request);

    if (result instanceof ACDLError) {
      await notifyAboutApiError(result, 'get_projects_failed');
      throw result;
    }

    if (result.pagesCount < result.pageNumber) {
      store.projectsPaging.pageNumber = result.pagesCount;
      await getProjectsAsync(workspaceId);
    } else {
      store.projectsList.replace(
        result.data.map((x) => ({
          ...x,
        }))
      );

      store.projectsPaging = {
        ...store.projectsPaging,
        orderBy,
        orderType,
        pageNumber: result.pageNumber,
        pagesCount: result.pagesCount,
        totalCount: result.totalCount,
      };
    }
  };

  const getProjectExclusiveDatasetsAsync = async (projectId: string): Promise<string[]> => {
    const result = await projectsApiService.getProjectExclusiveDatasetsAsync({ projectId });
    if (result instanceof ACDLError) {
      await notifyAboutApiError(result, 'get_exclusive_datasets_failed');
      throw result;
    }

    return result;
  };

  const deleteProjectAsync = async (projectId: string, deleteDatasets: boolean): Promise<void> => {
    const result = await projectsApiService.deleteProjectAsync({
      projectId,
      deleteDatasets,
    });

    if (result instanceof ACDLError) {
      await notifyAboutApiError(result, 'project_deleting_failed');
      throw result;
    }

    await refreshAsync();
  };

  const importProjectAsync = async (projectId: string, importAnnotations: boolean, importImageSets: boolean, file: File): Promise<boolean> => {
    const { currentWorkspace } = currentWorkspaceStore;
    if (!currentWorkspace.id) {
      return false;
    }

    const result = await projectsApiService.importProjectAsync({
      projectId,
      importAnnotations,
      importImageSets,
      file,
      workspaceId: currentWorkspace.id,
    });

    if (result instanceof ACDLError) {
      await notifyAboutApiError(result, 'project_import_failed');
      return false;
    }
    return true;
  };

  const notifyAboutApiError = async (result: ACDLError, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> => {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  };

  return {
    store,
    changeOwnershipFilter,
    refreshAsync,
    importProjectAsync,
    deleteProjectAsync,
    getProjectExclusiveDatasetsAsync,
    changeOrder,
    changePagination,
  };
};
